import React, { useState, useMemo } from "react";
import SEO from "components/SEO";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithVerticalPadding } from "components/misc/Layouts";
import { SectionHeading } from "components/misc/Headings";
import { useGetPhotos } from "data/useGetPhotos";
import PhotoDetail from "./PhotoDetail";
import { motion } from "framer-motion";
import Layout from "components/layouts/Layout";
import { getAbsoluteUrl } from "utils/urlUtils";
import { ReactComponent as HeartIcon } from "feather-icons/dist/icons/heart.svg";

const Section = tw(ContentWithVerticalPadding)``;
const SectionHeadingStyled = tw(SectionHeading)`text-center`;
const GalleryContainer = tw.div`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12`;

const PhotoSetCard = styled(motion.div)`
  ${tw`relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 cursor-pointer bg-gray-100`}
  aspect-ratio: 1 / 1;

  &:hover {
    ${tw`shadow-2xl`}
  }
`;

const PhotoSetImage = styled.img`
  ${tw`w-full h-full object-cover`}
  transition: transform 0.3s ease;

  ${PhotoSetCard}:hover & {
    transform: scale(1.05);
  }
`;

const PhotoSetOverlay = styled.div`
  ${tw`absolute inset-0 bg-black bg-opacity-50 transition-all duration-300 flex items-center justify-center`}
`;

const PhotoSetInfo = styled.div`
  ${tw`text-white text-center opacity-100 transition-opacity duration-300`}
`;

const PhotoSetTitle = tw.h3`text-xl font-bold mb-2`;
const PhotoSetCount = tw.p`text-sm`;

const LikeBadge = styled.div`
  ${tw`absolute bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1`}
  top: 8px;
  right: 8px;
`;

const LoadingContainer = tw.div`flex items-center justify-center py-16`;
const LoadingText = tw.p`text-gray-600 text-lg`;

const ErrorContainer = tw.div`bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg`;
const ErrorText = tw.p``;

/**
 * PhotoGalleryPage Component
 * Displays photos grouped by name/set
 * Click on a set to view photos in detail
 */
export default function PhotoGalleryPage() {
  const { data: photos, isLoading, error } = useGetPhotos();
  const [selectedPhotoSet, setSelectedPhotoSet] = useState(null);

  // Group photos by name
  const photoSets = useMemo(() => {
    if (!photos || photos.length === 0) return [];

    const sets = {};
    photos.forEach((photo) => {
      const setName = photo.title || photo.name || "Unnamed";
      if (!sets[setName]) {
        sets[setName] = [];
      }
      sets[setName].push(photo);
    });

    // Sort by count descending and return as array
    return Object.entries(sets)
      .map(([name, items]) => ({
        name,
        photos: items,
        thumbnail: items[0], // First photo as thumbnail
        totalLikes: items.reduce(
          (sum, photo) => sum + (photo.likeCount || 0),
          0
        ),
      }))
      .sort((a, b) => b.photos.length - a.photos.length);
  }, [photos]);

  if (isLoading) {
    return (
      <AnimationRevealPage>
        <Section>
          <LoadingContainer>
            <LoadingText>Loading photo gallery...</LoadingText>
          </LoadingContainer>
        </Section>
      </AnimationRevealPage>
    );
  }

  if (error) {
    return (
      <AnimationRevealPage>
        <Section>
          <Container>
            <ErrorContainer>
              <ErrorText>
                Failed to load photos: {error.message || "Unknown error"}
              </ErrorText>
            </ErrorContainer>
          </Container>
        </Section>
      </AnimationRevealPage>
    );
  }

  return (
    <Layout>
      <SEO title="Photo Gallery" description="Browse photos from Jamia Mohammadia Arabia events, programs, and campus life in Rayadurg." path="/photo-gallery" />
      <Section>
        <Container>
          <SectionHeadingStyled>Jamia Photo Gallery</SectionHeadingStyled>
          <p tw="text-center text-gray-600 mt-4 max-w-2xl mx-auto">
            Browse our collection of photos grouped by event or theme. Click on
            any set to view the full collection and interact with photos.
          </p>

          <GalleryContainer>
            {photoSets.map((set, index) => (
              <PhotoSetCard
                key={set.name}
                onClick={() => setSelectedPhotoSet(set)}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
              >
                {set.thumbnail?.imageUrl ? (
                  <>
                    <PhotoSetImage
                      src={getAbsoluteUrl(set.thumbnail.imageUrl)}
                      alt={set.name}
                    />
                    <LikeBadge>
                      <HeartIcon tw="w-4 h-4" />
                      {set.totalLikes}
                    </LikeBadge>
                    <PhotoSetOverlay>
                      <PhotoSetInfo>
                        <PhotoSetTitle>{set.name}</PhotoSetTitle>
                        <PhotoSetCount>
                          {set.photos.length}{" "}
                          {set.photos.length === 1 ? "photo" : "photos"}
                        </PhotoSetCount>
                      </PhotoSetInfo>
                    </PhotoSetOverlay>
                  </>
                ) : (
                  <PhotoSetOverlay>
                    <PhotoSetInfo>
                      <PhotoSetTitle>{set.name}</PhotoSetTitle>
                      <PhotoSetCount>
                        {set.photos.length}{" "}
                        {set.photos.length === 1 ? "photo" : "photos"}
                      </PhotoSetCount>
                    </PhotoSetInfo>
                  </PhotoSetOverlay>
                )}
              </PhotoSetCard>
            ))}
          </GalleryContainer>

          {photoSets.length === 0 && (
            <LoadingContainer>
              <LoadingText>No photos available</LoadingText>
            </LoadingContainer>
          )}
        </Container>
      </Section>

      {/* Photo Detail Modal */}
      {selectedPhotoSet && (
        <PhotoDetail
          photoSet={selectedPhotoSet}
          isOpen={!!selectedPhotoSet}
          onClose={() => setSelectedPhotoSet(null)}
        />
      )}
    </Layout>
  );
}
